import { useState } from "react";
import { apiPost } from "./api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2, Loader2, TrendingUp } from "lucide-react";
import AdminPageLayout from "./AdminPageLayout";

export default function RankingsManagement() {
  const [status, setStatus] = useState<"idle" | "running" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [processed, setProcessed] = useState(0);

  const triggerRebuild = async () => {
    setStatus("running");
    setMessage("Rebuilding rankings...");

    try {
      // Use ADMIN_API_KEY for cron endpoint authentication
      const adminKey = import.meta.env.VITE_ADMIN_API_KEY;
      if (!adminKey) {
        throw new Error("VITE_ADMIN_API_KEY is not configured");
      }

      const res = await fetch("/api/cron/rankings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${adminKey}`,
        },
        body: JSON.stringify({}),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Ranking rebuild failed: ${res.status} - ${errorText}`);
      }

      const data = await res.json();
      const processed = data.item?.processed || data.processed || 0;
      setProcessed(processed);
      setStatus("success");
      setMessage(`Successfully processed ${processed} items`);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setStatus("error");
      setMessage(errorMessage);
    }
  };

  return (
    <AdminPageLayout
      title="Rankings Management"
      description="Manually trigger ranking aggregation"
      icon={<TrendingUp className="w-8 h-8" />}
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Rebuild Rankings
          </CardTitle>
          <CardDescription>
            This will recalculate all rankings based on engagement scores (views, likes, comments).
            Rankings are normally updated automatically at 00:00 UTC daily.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={triggerRebuild}
            disabled={status === "running"}
            size="lg"
            data-testid="button-rebuild-rankings"
          >
            {status === "running" && <Loader2 className="mr-2 w-4 h-4 animate-spin" />}
            {status === "running" ? "Rebuilding..." : "Rebuild Rankings Now"}
          </Button>

          {status !== "idle" && (
            <div
              className={`flex items-start gap-3 p-4 rounded-md border ${
                status === "success"
                  ? "bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800"
                  : status === "error"
                  ? "bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800"
                  : "bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800"
              }`}
              data-testid="status-message"
            >
              {status === "success" && (
                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              )}
              {status === "error" && (
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              )}
              {status === "running" && (
                <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5 animate-spin" />
              )}

              <div className="flex-1">
                <p className="font-medium">{message}</p>
                {status === "success" && processed > 0 && (
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white dark:bg-gray-900 p-3 rounded border">
                      <div className="text-xs text-muted-foreground">Total Processed</div>
                      <div className="text-2xl font-bold">{processed}</div>
                    </div>
                    <div className="bg-white dark:bg-gray-900 p-3 rounded border">
                      <div className="text-xs text-muted-foreground">Status</div>
                      <Badge variant="default" className="mt-1">
                        Complete
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="text-sm text-muted-foreground space-y-2 border-t pt-4">
            <p className="font-medium">How Rankings Work:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Rankings are calculated based on engagement score: (views × 1) + (likes × 5) + (comments × 10)</li>
              <li>Top 100 items per content type (articles, videos, community talk, market listings)</li>
              <li>Available periods: daily, weekly, monthly</li>
              <li>Automatically updates daily at 00:00 UTC via GitHub Actions</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </AdminPageLayout>
  );
}
